/**
 * Inbox row and batch view model (PROD-3204, PM slice 2): turns an `InboxDocument` into the words, glyphs, chips and
 * actions of the patient chart's Documents inbox, board N8 (canvas "Lab result entry and AI report review", 20 Oct).
 * Pure, so the table only draws it. Ported from Practitioner `src/intake/lib/inbox-view.ts`, with the PM board's words
 * and the labs scope of 18 Oct: lab reports open review; omics and genomic reports are filed as documents only (no row
 * review yet), as is "other".
 *
 * An enum value this file does not know (the server may add values) shows as it is and offers no action.
 */
import type { GlyphKind } from '@/shared/ui/components/status-glyph';
import type { InboxDocument } from '../types';
import { patientMatch } from './identity-text';

export type ChipSemantic = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface Chip {
  label: string;
  semantic: ChipSemantic;
}

export interface Glyph {
  label: string;
  kind: GlyphKind;
}

export type RowAction =
  | { kind: 'review' }
  | { kind: 'add' }
  | { kind: 'confirm' }
  | { kind: 'retry' }
  | { kind: 'disregard' }
  | { kind: 'wait' };

export interface RowView {
  /** "Recognized as": the type and an optional meta line. */
  recognized: { label: string; sub: string | null };
  /** "Patient check": a glyph and its words; null while there is nothing to say. */
  identity: Glyph | null;
  /** "Status": a soft chip, or a glyph while the file is being read. */
  status: { chip: Chip } | { glyph: Glyph } | null;
  actions: RowAction[];
  /** The patient on the report is another patient: the row is held. */
  held: boolean;
}

export const DOC_TYPE_LABEL: Record<string, string> = {
  lab_report: 'Lab report',
  omics_report: 'Omics report',
  genomic_report: 'Genomic report',
  other: 'Other',
  radiology_report: 'Radiology report',
  referral_letter: 'Referral letter',
  discharge_summary: 'Discharge summary',
  prescription: 'Prescription',
  id_or_insurance: 'ID or insurance',
  clinical_photo: 'Clinical photo',
};

/** The types that are filed as a document only (no row review in this slice). */
const FILE_ONLY = new Set(['omics_report', 'genomic_report', 'other']);

/**
 * The run's real steps (Patient `IntakeWorkflowFactory`: Recognize, Read, Identify, Classify, Extract, Map, Verify,
 * Present) folded into the board's five stages, so "step N of 5" only moves forward.
 */
export const STEP_STAGE: Record<string, number> = {
  Recognize: 1,
  Read: 2,
  Identify: 2,
  Classify: 3,
  Extract: 3,
  Map: 3,
  Verify: 4,
  Present: 5,
};

/**
 * The review page (board N4, PM slice 3) is registered at intakeReviewPath behind ?x=intake, so a ready reading
 * offers "Review values". Kept as a constant so the inbox can switch the action off again without a route change.
 */
export const REVIEW_PAGE_READY = true;

const KNOWN_STATUS = ['Queued', 'Running', 'AwaitingReview', 'Completed', 'Failed', 'Discarded'];
const KNOWN_IDENTITY = ['Verified', 'NeedsIdentityReview', 'Conflict', 'NotChecked'];

/** The review page (board N4, PM slice 3). */
export const intakeReviewPath = (patientId: string, runId: string) => `/patients/edit/${patientId}/intake/${runId}`;

/** The patient chart's Documents section, where the inbox is (it opens on the Inbox while files are waiting). */
export const intakeInboxPath = (patientId: string) => `/patients/edit/${patientId}?tab=chart&inner=documents`;

export const isInFlight = (d: InboxDocument) => d.status === 'Queued' || d.status === 'Running';

export const isImage = (d: InboxDocument) => d.contentType.toLowerCase().startsWith('image/');

export const docTypeLabel = (t: string | null) => (t === null ? '' : (DOC_TYPE_LABEL[t] ?? t));

const isFileOnly = (d: InboxDocument) => d.docType !== null && FILE_ONLY.has(d.docType);

/** A name-only match still waiting for someone to confirm the patient. `patientConfirmed` null is unknown: still waiting. */
const awaitsPatientConfirm = (d: InboxDocument) => d.identityStatus === 'NeedsIdentityReview' && d.patientConfirmed !== true;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "PDF · 2 pages", "PNG image". The page count is left out while it is null. */
export function fileSubLine(d: InboxDocument): string {
  const ct = d.contentType.toLowerCase();
  const kind = ct === 'application/pdf' ? 'PDF' : ct === 'image/png' ? 'PNG image' : ct === 'image/jpeg' ? 'JPG image' : ct;
  return d.pageCount === null ? kind : `${kind} · ${plural(d.pageCount, 'page', 'pages')}`;
}

/** A held file stops before recognition (no type, no page count): the row says why instead of an empty type. */
export const HELD_RECOGNIZED = 'Held: names another patient. Not read further.';

function recognized(d: InboxDocument): RowView['recognized'] {
  if (d.identityStatus === 'Conflict' && d.docType === null) return { label: HELD_RECOGNIZED, sub: null };
  if (d.docType === null) return { label: isInFlight(d) ? 'Recognizing' : '—', sub: null };
  const label = docTypeLabel(d.docType);
  if (isInFlight(d)) return { label, sub: 'reading' };
  if (d.docType === 'lab_report') {
    const n = d.rowCounts?.total;
    return { label, sub: n === undefined || n === null ? null : plural(n, 'value', 'values') };
  }
  if (d.docType === 'omics_report' || d.docType === 'genomic_report') {
    return { label, sub: 'filed as a document · discrete values later' };
  }
  return { label, sub: null };
}

/**
 * "Patient check" in three words a nurse or an admin understands (Samer, 20 Oct): Matches, Check details, Different
 * patient (`patientMatch`). While the file is still being read nothing was checked yet, so it says so instead.
 */
function identity(d: InboxDocument): Glyph | null {
  // No run: nothing was checked.
  if (d.identityStatus === null) return null;
  if (d.identityStatus === 'NotChecked' && isInFlight(d)) return { label: 'Checked after reading', kind: 'empty' };
  const m = patientMatch(d.identityStatus, d.patientConfirmed);
  return { label: m.label, kind: m.glyph };
}

function status(d: InboxDocument): RowView['status'] {
  if (d.identityStatus === 'Conflict') return { chip: { label: 'Held', semantic: 'error' } };
  if (d.status === null) return null;
  if (isInFlight(d)) {
    const at = d.step === null ? 0 : (STEP_STAGE[d.step] ?? 0);
    return { glyph: { label: d.status === 'Running' && at > 0 ? `Reading · step ${at} of 5` : 'Reading', kind: 'half' } };
  }
  if (awaitsPatientConfirm(d) && d.status === 'AwaitingReview') {
    return { chip: { label: 'Confirm patient', semantic: 'warning' } };
  }
  switch (d.status) {
    case 'Failed':
      return { chip: { label: 'Could not be read', semantic: 'error' } };
    case 'AwaitingReview':
      if (d.docType === 'lab_report') return { chip: { label: 'Ready for review', semantic: 'info' } };
      return { chip: { label: 'File only', semantic: 'neutral' } };
    case 'Completed':
      return { chip: { label: 'Added', semantic: 'success' } };
    case 'Discarded':
      return { chip: { label: 'Disregarded', semantic: 'neutral' } };
    default:
      return { chip: { label: d.status, semantic: 'neutral' } };
  }
}

function actionsFor(d: InboxDocument): RowAction[] {
  // No run yet (or any more): the file can only leave the inbox.
  if (d.status === null || d.identityStatus === null) return [{ kind: 'disregard' }];
  // An unknown status or identity value: show it as it is, offer nothing.
  if (!KNOWN_STATUS.includes(d.status) || !KNOWN_IDENTITY.includes(d.identityStatus)) return [];
  if (d.identityStatus === 'Conflict') return [{ kind: 'disregard' }];
  if (isInFlight(d)) return [{ kind: 'wait' }];
  if (awaitsPatientConfirm(d)) {
    // Until someone confirms the patient: "This is <first name>". Add does not check identity, so a file-only type
    // can be added too. Once `patientConfirmed` is true the row takes the usual actions below.
    const out: RowAction[] = d.runId ? [{ kind: 'confirm' }] : [];
    if (d.status === 'AwaitingReview' && isFileOnly(d)) out.push({ kind: 'add' });
    return [...out, { kind: 'disregard' }];
  }
  switch (d.status) {
    case 'Failed':
      return d.runId ? [{ kind: 'retry' }, { kind: 'disregard' }] : [{ kind: 'disregard' }];
    case 'AwaitingReview':
      if (d.docType === 'lab_report') {
        return d.runId && REVIEW_PAGE_READY ? [{ kind: 'review' }, { kind: 'disregard' }] : [{ kind: 'disregard' }];
      }
      if (isFileOnly(d)) return [{ kind: 'add' }, { kind: 'disregard' }];
      // A type outside the labs scope (an older row): it can only leave the inbox.
      return [{ kind: 'disregard' }];
    default:
      return [];
  }
}

/**
 * A lab-desk upload that could not be read and that the server has since filed to its order (Patient G4): a chart
 * document now, removed from the Documents screen, not from the inbox (42, 20 Oct).
 */
const FILED_TO_ORDER: Pick<RowView, 'status' | 'actions'> = {
  status: { chip: { label: 'Filed to the order', semantic: 'success' } },
  actions: [],
};

/**
 * A lab-desk upload that could not be read and that the server has filed to its order: the inbox row carries the filed
 * `patientDocumentId` (Patient, backend-repos-57). Holds for an older failed upload too, after a clearer copy.
 */
export const isFiledToOrder = (d: InboxDocument) =>
  d.status === 'Failed' && (d.labRequestIds?.length ?? 0) > 0 && !!d.patientDocumentId;

export function rowView(d: InboxDocument): RowView {
  if (isFiledToOrder(d)) {
    return { recognized: recognized(d), identity: identity(d), ...FILED_TO_ORDER, held: false };
  }
  return {
    recognized: recognized(d),
    identity: identity(d),
    status: status(d),
    actions: actionsFor(d),
    held: d.identityStatus === 'Conflict',
  };
}

// ---- Type picker (PROD-3339) ------------------------------------------------------------------------------------

/** The types a user may pick (labs scope; the server refuses any other new pick). */
export const PICKABLE_DOC_TYPES = ['lab_report', 'omics_report', 'genomic_report', 'other'] as const;

export interface TypePickerView {
  /** What the picker shows first: the stored type, else the suggestion, else nothing (the user must pick). */
  initial: string | null;
  /** The classifier's suggestion, for the "Suggested" mark; null when the row has a type or no suggestion. */
  suggested: string | null;
  /** The stored type (null while the document is untyped). Confirming it again changes nothing. */
  current: string | null;
}

/**
 * Whether the row offers the type picker, and what it starts on. Every open row that is not being read or held: an
 * untyped row (the user must pick), a row the classifier typed (the user may change it), a failed one (a lab pick then
 * retries the reading). Null for a row being read, a held row (another patient) and a row with no run.
 */
export function typePickerFor(d: InboxDocument): TypePickerView | null {
  if (d.inboxStatus !== 'Open' || d.status === null || d.runId === null) return null;
  if (isInFlight(d) || d.identityStatus === 'Conflict') return null;
  if (d.status !== 'AwaitingReview' && d.status !== 'Failed') return null;
  const suggested = d.docType === null ? (d.suggestedDocType ?? null) : null;
  return { initial: d.docType ?? suggested, suggested, current: d.docType };
}

/**
 * What Confirm type does with a picked type. `add`: the inbox Add with that type files it as a document (source
 * User). `read`: a lab report: the Add saves the pick and answers review_required, then the reading starts again
 * (Retry) when the run has no values yet, or review opens when it has them. `none`: nothing changed.
 */
export function confirmTypeAction(d: InboxDocument, picked: string | null): 'add' | 'read' | 'none' {
  if (picked === null || picked === d.docType) return 'none';
  return picked === 'lab_report' ? 'read' : 'add';
}

/** A lab report pick on a run with no values yet re-reads the file (Patient #1207 Retry); with values, review opens. */
export const labPickNeedsReading = (d: InboxDocument) => (d.rowCounts?.total ?? 0) === 0;

// ---- Batches ----------------------------------------------------------------------------------------------------

export interface BatchView {
  batchId: string;
  documents: InboxDocument[];
}

/** Groups by `batchId`, newest batch first (by the latest `uploadedAt` inside it); file order inside a batch is kept. */
export function groupByBatch(docs: InboxDocument[]): BatchView[] {
  const map = new Map<string, InboxDocument[]>();
  for (const d of docs) map.set(d.batchId, [...(map.get(d.batchId) ?? []), d]);
  const latest = (list: InboxDocument[]) => Math.max(...list.map((x) => parseUtc(x.uploadedAt).getTime() || 0));
  return [...map.entries()]
    .map(([batchId, documents]) => ({ batchId, documents }))
    .sort((a, b) => latest(b.documents) - latest(a.documents));
}

/** The Patient API sends UTC (`...Z`). A time with no zone is read as UTC too, never as local time. */
export const parseUtc = (iso: string) => new Date(/(?:[zZ]|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "9:12a, Oct 20, 2026", in the browser's time zone, as the board writes it. `iso` is UTC. */
export function droppedAt(iso: string): string | null {
  const t = parseUtc(iso);
  if (Number.isNaN(t.getTime())) return null;
  const h = t.getHours();
  const hour = h % 12 === 0 ? 12 : h % 12;
  const mm = String(t.getMinutes()).padStart(2, '0');
  return `${hour}:${mm}${h < 12 ? 'a' : 'p'}, ${MONTHS[t.getMonth()]} ${t.getDate()}, ${t.getFullYear()}`;
}

/** "Dropped 9:12a, Oct 20, 2026 by Rania Saeed". No name while `displayName` is null. */
export function batchHeading(docs: InboxDocument[]): string {
  const first = docs[0];
  const when = droppedAt(first.uploadedAt);
  const name = first.uploadedBy?.displayName;
  return `Dropped${when ? ` ${when}` : ''}${name ? ` by ${name}` : ''}`;
}

export const batchCount = (docs: InboxDocument[]) => plural(docs.length, 'file', 'files');

/**
 * The batch's one-line progress: "Recognize · Read · Place · Verify · Review: 2 waiting for review, 1 to add". Only lab
 * reports have a review step; omics, genomic and other files are counted as "to add".
 */
export function batchProgress(docs: InboxDocument[]): Glyph {
  const reading = docs.filter(isInFlight).length;
  const ready = docs.filter((d) => d.status === 'AwaitingReview' && d.identityStatus !== 'Conflict');
  const waiting = ready.filter((d) => d.docType === 'lab_report').length;
  const toAdd = ready.length - waiting;
  const held = docs.filter((d) => d.identityStatus === 'Conflict').length;
  const parts = [
    waiting && `${waiting} waiting for review`,
    toAdd && `${toAdd} to add`,
    reading && `${reading} reading`,
    held && `${held} held`,
  ].filter(Boolean);
  return {
    label: `Recognize · Read · Place · Verify · Review: ${parts.length ? parts.join(', ') : 'done'}`,
    kind: reading ? 'half' : 'done',
  };
}

// ---- Duplicate uploads ------------------------------------------------------------------------------------------

/**
 * Where a duplicate upload's link goes (bulk test, 20 Oct: the line promised a link and had none). From what the upload
 * reply (`duplicateOf`, else `documentId`), the open inbox and the inbox history (`status=all`) hold:
 * - a lab report waiting for review opens its review; any other file still in the inbox is its row;
 * - a file the history says was Added is under Filed documents, when the page can show them;
 * - any other file out of the inbox (disregarded, or its fate not known) is "gone": it says so, with no link, because a
 *   disregarded file is not filed.
 * No id, or the open inbox not read yet: no link and nothing more to say.
 */
export type DuplicateLink =
  | { kind: 'review'; runId: string }
  | { kind: 'inbox'; documentId: string }
  | { kind: 'filed' }
  | { kind: 'gone' }
  | { kind: 'none' };

export function duplicateLink(
  originalId: string | null | undefined,
  inbox: InboxDocument[] | undefined,
  history: InboxDocument[] | undefined,
  canShowFiled: boolean,
): DuplicateLink {
  if (!originalId || !inbox) return { kind: 'none' };
  const d = inbox.find((x) => x.documentId === originalId);
  if (d) {
    // The same rule as the row's Review values action: a lab report waiting for review, patient not in doubt.
    const reviewable =
      d.docType === 'lab_report' && d.status === 'AwaitingReview' && d.identityStatus !== 'Conflict' && !awaitsPatientConfirm(d);
    return reviewable && d.runId && REVIEW_PAGE_READY ? { kind: 'review', runId: d.runId } : { kind: 'inbox', documentId: d.documentId };
  }
  const filed = history?.find((x) => x.documentId === originalId)?.inboxStatus === 'Added';
  return filed && canShowFiled ? { kind: 'filed' } : { kind: 'gone' };
}

/** A duplicate whose earlier file is not in the open inbox: the history is read to know whether it was filed. */
export const needsInboxHistory = (originalId: string | null | undefined, inbox: InboxDocument[] | undefined) =>
  !!originalId && !!inbox && !inbox.some((x) => x.documentId === originalId);

/** The anchor id of an inbox row, so a duplicate can point at it. */
export const inboxRowAnchor = (documentId: string) => `intake-doc-${documentId}`;
